import { useCallback, useMemo, useState } from "react";
import type { Source } from "@onetasklist/shared";
import { useConfig } from "@contexts/ConfigContext";
import {
  useConnectionsViewModel,
  type ConnectionEditState,
} from "../../board/connections/Connections.ViewModel";
import type { ConnectToolsStepViewModel, ConnectionSubPanelState } from "./ConnectToolsStep.ViewModel.types";

const buildInitialDraft = (source: Source): ConnectionEditState => ({
  id: "",
  sourceTypeId: source.id,
  name: source.displayName,
  colour: "#4f7cac",
  defaultStatusId: "",
  archived: false,
  manageTasksHere: true,
  defaultToTop: false,
  tagName: "",
  newTaskDaysLimit: "",
  profileName: "",
  profileId: "",
  isAuthorised: false,
  configuration: {},
});

export const useConnectToolsStepViewModel = (): ConnectToolsStepViewModel => {
  const connectionsVm = useConnectionsViewModel();
  const { config } = useConfig();
  const [subPanel, setSubPanel] = useState<ConnectionSubPanelState | null>(null);

  const connections = useMemo(
    () => connectionsVm.connections.filter((connection) => !connection.archived),
    [connectionsVm.connections],
  );

  const openSubPanel = useCallback((source: Source): void => {
    setSubPanel({ source, draft: buildInitialDraft(source) });
  }, []);

  const closeSubPanel = useCallback((): void => {
    setSubPanel(null);
  }, []);

  const updateDraft = useCallback((update: Partial<ConnectionEditState>): void => {
    setSubPanel((prev) =>
      prev ? { ...prev, draft: { ...prev.draft, ...update } } : prev,
    );
  }, []);

  const connectSource = useCallback(async (): Promise<void> => {
    if (!subPanel) return;
    await connectionsVm.connectSource(subPanel.source.id);
    closeSubPanel();
  }, [closeSubPanel, connectionsVm, subPanel]);

  const editingSource = useMemo(
    () =>
      connectionsVm.allSources.find(
        (source) => source.id === connectionsVm.editingConnection?.sourceTypeId,
      ) ?? null,
    [connectionsVm.allSources, connectionsVm.editingConnection],
  );

  const onEditConnection = useCallback(
    (connectionId: string): void => {
      const connection = connectionsVm.connections.find((c) => c.id === connectionId);
      if (connection) {
        connectionsVm.openConnectionSettings(connection);
      }
    },
    [connectionsVm],
  );

  return {
    isLoading: connectionsVm.isLoading,
    isConnecting: connectionsVm.isCreatingConnection || connectionsVm.isAuthorisingConnection,
    allSources: connectionsVm.allSources,
    connections,
    iconBaseUrl: config?.regionBaseUrl ?? "",
    searchText: connectionsVm.searchText,
    subPanel,
    hasVotedForSource: connectionsVm.hasVotedForSource,
    setSearchText: connectionsVm.setSearchText,
    voteForSource: connectionsVm.voteForSource,
    openSubPanel,
    closeSubPanel,
    updateDraft,
    connectSource,
    editingSource,
    editingConnection: connectionsVm.editingConnection,
    isSavingEdit: connectionsVm.isSavingConnection,
    isReauthorisingEdit: connectionsVm.isAuthorisingConnection,
    isDeletingEdit: connectionsVm.isDeletingConnection,
    onEditConnection,
    onUpdateEditingConnection: connectionsVm.updateEditingConnection,
    onSaveEditingConnection: connectionsVm.saveEditingConnection,
    onCloseEditingConnection: connectionsVm.closeConnectionSettings,
    onReauthoriseEditingConnection: connectionsVm.reAuthoriseEditingConnection,
    onDeleteEditingConnection: connectionsVm.deleteEditingConnection,
    confirmDialog: connectionsVm.confirmDialog,
    selectOrgDialog: connectionsVm.selectOrgDialog,
    billingRefusal: connectionsVm.billingRefusal,
    clearBillingRefusal: connectionsVm.clearBillingRefusal,
  };
};
